import { useState, useCallback, useMemo } from "react";
import { isAfter, isEqual } from "date-fns";

import { getRegionalSettingsOptions, isoToUTCString } from "shared/utils";
import { DateChangeProps } from "shared/types";

interface Props {
  includeEqual?: boolean;
  date?: string;
  time?: string;
  timeZone?: string;
  onDateChange?: (time?: string) => void;
}
export const useIsDateInFuture = ({
  includeEqual = false,
  date,
  time,
  timeZone,
  onDateChange,
}: Props) => {
  const [checkDate, setCheckDate] = useState<DateChangeProps | null>({
    date,
    time,
    timeZone,
  });

  const handleDateChange = useCallback(
    ({ date, time, timeZone }: DateChangeProps) => {
      const dateTime = {
        date: date || checkDate?.date,
        time: time || checkDate?.time,
        timeZone: timeZone || checkDate?.timeZone,
      };

      onDateChange && onDateChange(time);
      setCheckDate(dateTime);
    },
    [checkDate?.date, checkDate?.time, checkDate?.timeZone, onDateChange]
  );

  const isDateInFuture = useMemo(() => {
    const { timezoneToUtc } = getRegionalSettingsOptions();

    if (!checkDate?.date) return true;

    const date = new Date(
      isoToUTCString({
        iso: checkDate?.date,
        hour: checkDate?.time,
        timeZone: checkDate?.timeZone ? timezoneToUtc(checkDate?.timeZone) : undefined,
      })
    );

    const today = new Date();

    return isAfter(date, today) || (includeEqual && isEqual(date, today));
  }, [checkDate?.date, checkDate?.time, checkDate?.timeZone, includeEqual]);

  return {
    isDateInFuture,
    handleDateChange,
  };
};
